/**
 * StockfishAnalysisManager.js - Centralized analysis coordination
 * Handles analysis requests, cancellation, caching, and state management
 */

import StockfishAnalysisRunner from './StockfishAnalysisRunner';

class StockfishAnalysisManager {
  constructor() {
    this.currentRequest = null;
    this.runner = new StockfishAnalysisRunner();
    this.isAnalyzing = false;
    this.analysisCache = new Map();
    this.listeners = new Set();
    
    // Hardcoded analysis for starting position
    this.startingPositionAnalysis = {
      depth: 15,
      positionFEN: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
      timestamp: Date.now(),
      pvs: [
        {
          line: 1,
          evaluation: '+0.30',
          normalizedCP: 0.30,
          bestMoveLAN: 'e2e4',
          bestMoveSAN: 'e4',
          pv: ['e2e4', 'e7e5', 'g1f3', 'b8c6', 'f1b5'],
          pvSAN: ['e4', 'e5', 'Nf3', 'Nc6', 'Bb5']
        },
        {
          line: 2,
          evaluation: '+0.25',
          normalizedCP: 0.25,
          bestMoveLAN: 'd2d4',
          bestMoveSAN: 'd4',
          pv: ['d2d4', 'd7d5', 'c2c4', 'e7e6', 'b1c3'],
          pvSAN: ['d4', 'd5', 'c4', 'e6', 'Nc3']
        },
        {
          line: 3,
          evaluation: '+0.20',
          normalizedCP: 0.20,
          bestMoveLAN: 'g1f3',
          bestMoveSAN: 'Nf3',
          pv: ['g1f3', 'd7d5', 'd2d4', 'g8f6', 'c2c4'],
          pvSAN: ['Nf3', 'd5', 'd4', 'Nf6', 'c4']
        }
      ]
    };
    
    // Initialize runner
    this.runner.initialize().then(() => {
      
    }).catch(error => {
      console.error('❌ StockfishAnalysisManager: Runner initialization failed:', error);
    });
  }
  
  /**
   * Add a listener for analysis updates
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
  
  /**
   * Notify all listeners of analysis updates
   */
  notifyListeners(analysis, moveId) {
    this.listeners.forEach(listener => {
      try {
        listener(analysis, moveId);
      } catch (error) {
        console.error('❌ StockfishAnalysisManager: Error in listener:', error);
      }
    });
  }
  
  /**
   * Get cache key for a FEN position
   */
  getCacheKey(fen) {
    // Only use the first 4 parts of FEN (position data, ignore move counters)
    return fen.split(' ').slice(0, 4).join(' ');
  }
  
  /**
   * Check if analysis is cached for a FEN
   */
  hasCachedAnalysis(fen) {
    const key = this.getCacheKey(fen);
    return this.analysisCache.has(key);
  }
  
  /**
   * Get cached analysis for a FEN
   */
  getCachedAnalysis(fen) {
    const key = this.getCacheKey(fen);
    return this.analysisCache.get(key);
  }
  
  /**
   * Cache analysis result for a FEN
   */
  setCachedAnalysis(fen, analysis) {
    const key = this.getCacheKey(fen);
    this.analysisCache.set(key, {
      ...analysis,
      timestamp: Date.now()
    });
    
    // Limit cache size to prevent memory leaks
    if (this.analysisCache.size > 1000) {
      const firstKey = this.analysisCache.keys().next().value;
      this.analysisCache.delete(firstKey);
    }
  }
  
  /**
   * Check if position is the starting position
   */
  isStartingPosition(fen) {
    const startingFEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    return this.getCacheKey(fen) === this.getCacheKey(startingFEN);
  }
  
  /**
   * Cancel current analysis if running
   */
  cancelCurrentAnalysis() {
    if (this.currentRequest) {
      
      this.currentRequest.cancel();
      this.currentRequest = null;
      this.isAnalyzing = false;
    }
  }
  
  /**
   * Main analysis request method
   * @param {string} fen - Position to analyze
   * @param {string} moveId - ID of the move requesting analysis
   * @param {Object} moveObj - Move object to store analysis in
   * @param {Function} updateCallback - Callback to update move object
   */
  async requestAnalysis(fen, moveId, moveObj, updateCallback) {
    if (!fen || !moveId) {
      console.warn('⚠️ StockfishAnalysisManager: Invalid analysis request - missing fen or moveId');
      return null;
    }
    
    
    
    // Check if move already has analysis
    if (moveObj && moveObj.analysis) {
      
      this.notifyListeners(moveObj.analysis, moveId);
      return moveObj.analysis;
    }
    
    // Check for starting position
    if (this.isStartingPosition(fen)) {
      
      const analysis = { ...this.startingPositionAnalysis };
      
      // Store in move object if provided
      if (moveObj && updateCallback) {
        updateCallback(moveId, { analysis });
      }
      
      this.notifyListeners(analysis, moveId);
      return analysis;
    }
    
    // Check cache
    if (this.hasCachedAnalysis(fen)) {
      
      const cachedAnalysis = this.getCachedAnalysis(fen);
      
      // Store in move object if provided
      if (moveObj && updateCallback) {
        updateCallback(moveId, { analysis: cachedAnalysis });
      }
      
      this.notifyListeners(cachedAnalysis, moveId);
      return cachedAnalysis;
    }
    
    // Cancel any existing analysis
    this.cancelCurrentAnalysis();
    
    // Create new analysis request
    this.isAnalyzing = true;
    const abortController = new AbortController();
    
    this.currentRequest = {
      moveId,
      fen,
      signal: abortController.signal,
      cancel: () => {
        abortController.abort();
        this.runner.stopAnalysis();
      }
    };
    
    try {
      
      
      // Start analysis
      const analysis = await this.runner.analyzePosition(fen, {
        depth: 15,
        multiPV: 3,
        signal: abortController.signal
      });
      
      // Check if request was cancelled or moveId changed
      if (abortController.signal.aborted) {
        
        return null;
      }
      
      if (this.currentRequest && this.currentRequest.moveId !== moveId) {
        
        return null;
      }
      
      
      
      // Cache the result
      this.setCachedAnalysis(fen, analysis);
      
      // Store in move object if provided
      if (moveObj && updateCallback) {
        updateCallback(moveId, { analysis });
      }
      
      // Clear current request
      this.currentRequest = null;
      this.isAnalyzing = false;
      
      // Notify listeners
      this.notifyListeners(analysis, moveId);
      
      return analysis;
      
    } catch (error) {
      this.currentRequest = null;
      this.isAnalyzing = false;
      
      if (error.name === 'AbortError') {
        
        return null;
      }
      
      console.error(`❌ StockfishAnalysisManager: Analysis failed for move ${moveId}:`, error);
      throw error;
    }
  }
  
  /**
   * Get current analysis state
   */
  getState() {
    return {
      isAnalyzing: this.isAnalyzing,
      currentMoveId: this.currentRequest?.moveId || null,
      cacheSize: this.analysisCache.size
    };
  }
  
  /**
   * Clear all cached analysis
   */
  clearCache() {
    this.analysisCache.clear();
    
  }
  
  /**
   * Destroy the manager and clean up resources
   */
  destroy() {
    this.cancelCurrentAnalysis();
    this.listeners.clear();
    this.analysisCache.clear();
    this.runner.destroy();
    
  }
}

// Create singleton instance
const analysisManager = new StockfishAnalysisManager();

export default analysisManager; 